import React, { useState } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SFCReport {
  id: string;
  name: string;
  description: string;
  frequency: 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly';
  nextDue: string;
  lastSubmitted: string;
  status: 'Submitted' | 'Pending' | 'Overdue' | 'Draft';
  regulation: string;
  recordCount?: number;
  submissionHistory: { date: string; status: string; records?: number }[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const REPORTS: SFCReport[] = [
  {
    id: 'r1',
    name: 'Daily Position Report (OTC)',
    description:
      'Daily end-of-day OTC derivatives position report including all outstanding notional, MTM, and delta-equivalent exposures across all asset classes.',
    frequency: 'Daily',
    nextDue: '2026-02-21',
    lastSubmitted: '2026-02-20',
    status: 'Submitted',
    regulation: 'SFO s.101',
    recordCount: 2847,
    submissionHistory: [
      { date: '2026-02-20', status: 'Submitted', records: 2847 },
      { date: '2026-02-19', status: 'Submitted', records: 2801 },
      { date: '2026-02-18', status: 'Submitted', records: 2798 },
      { date: '2026-02-17', status: 'Submitted', records: 2815 },
      { date: '2026-02-14', status: 'Submitted', records: 2763 },
    ],
  },
  {
    id: 'r2',
    name: 'Large Exposure Report',
    description:
      'Weekly aggregated report of counterparty exposures exceeding 10% of capital base, per HKMA large exposure framework.',
    frequency: 'Weekly',
    nextDue: '2026-02-23',
    lastSubmitted: '2026-02-17',
    status: 'Submitted',
    regulation: 'HKMA CR-G-2',
    recordCount: 12,
    submissionHistory: [
      { date: '2026-02-17', status: 'Submitted', records: 12 },
      { date: '2026-02-10', status: 'Submitted', records: 11 },
      { date: '2026-02-03', status: 'Submitted', records: 13 },
      { date: '2026-01-27', status: 'Submitted', records: 10 },
      { date: '2026-01-20', status: 'Submitted', records: 14 },
    ],
  },
  {
    id: 'r3',
    name: 'Net Open Position Report',
    description:
      'Daily FX and commodity net open position report for regulatory capital calculation. Report covers all CCY pairs with NOP >1% of capital.',
    frequency: 'Daily',
    nextDue: '2026-02-20',
    lastSubmitted: '2026-02-19',
    status: 'Pending',
    regulation: 'SFO s.135',
    recordCount: undefined,
    submissionHistory: [
      { date: '2026-02-19', status: 'Submitted', records: 34 },
      { date: '2026-02-18', status: 'Submitted', records: 31 },
      { date: '2026-02-17', status: 'Submitted', records: 33 },
      { date: '2026-02-14', status: 'Submitted', records: 29 },
      { date: '2026-02-13', status: 'Submitted', records: 32 },
    ],
  },
  {
    id: 'r4',
    name: 'Trade Repository Report (HKTR)',
    description:
      'Daily OTC derivatives trade reporting to Hong Kong Trade Repository (HKTR) per mandatory reporting obligation under OTC Clearing Rules.',
    frequency: 'Daily',
    nextDue: '2026-02-21',
    lastSubmitted: '2026-02-20',
    status: 'Submitted',
    regulation: 'OTC Clearing Rules Cap. 571',
    recordCount: 156,
    submissionHistory: [
      { date: '2026-02-20', status: 'Submitted', records: 156 },
      { date: '2026-02-19', status: 'Submitted', records: 143 },
      { date: '2026-02-18', status: 'Submitted', records: 128 },
      { date: '2026-02-17', status: 'Submitted', records: 167 },
      { date: '2026-02-14', status: 'Submitted', records: 149 },
    ],
  },
  {
    id: 'r5',
    name: 'Liquidity Return (LR-1)',
    description:
      'Monthly liquidity return covering LCR, NSFR, liquidity risk appetite statement, and stress testing results per HKMA liquidity guidelines.',
    frequency: 'Monthly',
    nextDue: '2026-03-15',
    lastSubmitted: '2026-02-06',
    status: 'Submitted',
    regulation: 'HKMA CR-L-1',
    recordCount: 1,
    submissionHistory: [
      { date: '2026-02-06', status: 'Submitted', records: 1 },
      { date: '2026-01-08', status: 'Submitted', records: 1 },
      { date: '2025-12-05', status: 'Submitted', records: 1 },
      { date: '2025-11-07', status: 'Submitted', records: 1 },
      { date: '2025-10-09', status: 'Submitted', records: 1 },
    ],
  },
  {
    id: 'r6',
    name: 'Capital Adequacy Return',
    description:
      'Monthly return covering capital base, risk-weighted assets, Tier 1/Tier 2 capital ratios, and capital buffer requirements under Basel III framework.',
    frequency: 'Monthly',
    nextDue: '2026-03-15',
    lastSubmitted: '2026-02-06',
    status: 'Submitted',
    regulation: 'HKMA CR-L-2',
    recordCount: 1,
    submissionHistory: [
      { date: '2026-02-06', status: 'Submitted', records: 1 },
      { date: '2026-01-08', status: 'Submitted', records: 1 },
      { date: '2025-12-05', status: 'Submitted', records: 1 },
      { date: '2025-11-07', status: 'Submitted', records: 1 },
      { date: '2025-10-09', status: 'Submitted', records: 1 },
    ],
  },
  {
    id: 'r7',
    name: 'EMIR Refit Reconciliation',
    description:
      'Weekly EU EMIR Refit reconciliation report for EU-facing OTC derivatives trades, covering UTI matching, LEI validation, and data quality checks.',
    frequency: 'Weekly',
    nextDue: '2026-02-19',
    lastSubmitted: '2026-02-12',
    status: 'Overdue',
    regulation: 'EU EMIR Refit 2024',
    recordCount: undefined,
    submissionHistory: [
      { date: '2026-02-12', status: 'Submitted', records: 89 },
      { date: '2026-02-05', status: 'Submitted', records: 92 },
      { date: '2026-01-29', status: 'Late', records: 85 },
      { date: '2026-01-22', status: 'Submitted', records: 91 },
      { date: '2026-01-15', status: 'Submitted', records: 88 },
    ],
  },
  {
    id: 'r8',
    name: 'Client Asset Return',
    description:
      'Monthly client asset segregation return confirming compliance with client money rules, asset segregation obligations, and reconciliation sign-off.',
    frequency: 'Monthly',
    nextDue: '2026-03-15',
    lastSubmitted: '2026-02-06',
    status: 'Submitted',
    regulation: 'SFO s.104 / CCRR',
    recordCount: 1,
    submissionHistory: [
      { date: '2026-02-06', status: 'Submitted', records: 1 },
      { date: '2026-01-08', status: 'Submitted', records: 1 },
      { date: '2025-12-05', status: 'Submitted', records: 1 },
      { date: '2025-11-07', status: 'Submitted', records: 1 },
      { date: '2025-10-09', status: 'Submitted', records: 1 },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusStyle(s: SFCReport['status']): { bg: string; text: string; dot: string } {
  switch (s) {
    case 'Submitted': return { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' };
    case 'Pending':   return { bg: 'bg-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-500' };
    case 'Overdue':   return { bg: 'bg-red-100',     text: 'text-red-700',     dot: 'bg-red-500' };
    case 'Draft':     return { bg: 'bg-slate-100',   text: 'text-slate-600',   dot: 'bg-slate-400' };
  }
}

function freqColor(f: SFCReport['frequency']): string {
  switch (f) {
    case 'Daily':     return 'bg-rose-100 text-rose-700';
    case 'Weekly':    return 'bg-blue-100 text-blue-700';
    case 'Monthly':   return 'bg-purple-100 text-purple-700';
    case 'Quarterly': return 'bg-indigo-100 text-indigo-700';
  }
}

function histStatusStyle(s: string): { bg: string; text: string } {
  if (s === 'Submitted') return { bg: 'bg-emerald-100', text: 'text-emerald-700' };
  if (s === 'Late') return { bg: 'bg-amber-100', text: 'text-amber-700' };
  return { bg: 'bg-slate-100', text: 'text-slate-600' };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SFCReportingView() {
  const [selected, setSelected] = useState<SFCReport>(REPORTS[2]);

  const submitted = REPORTS.filter(r => r.status === 'Submitted').length;
  const submittedToday = REPORTS.filter(r => r.status === 'Submitted' && r.lastSubmitted === '2026-02-20').length;
  const pending = REPORTS.filter(r => r.status === 'Pending').length;
  const overdue = REPORTS.filter(r => r.status === 'Overdue').length;

  return (
    <div className="flex flex-col h-full bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">SFC Regulatory Reporting</h1>
            <p className="text-xs text-slate-500">Type 11 — OTC Derivatives Compliance · Hong Kong SFC / HKMA</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {overdue > 0 && (
              <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
                <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-xs font-semibold text-red-700">{overdue} Report Overdue</span>
              </div>
            )}
            <span className="text-xs text-slate-400">2026-02-20 · HKT</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Stat Cards */}
        <div className="grid grid-cols-4 gap-4 p-6 pb-0">
          <ReportStatCard label="Total Reports" value="8" sub="Regulatory obligations" color="rose" />
          <ReportStatCard label="Submitted Today" value={String(submittedToday)} sub="On-time submissions" color="green" />
          <ReportStatCard label="Pending" value={String(pending)} sub="Require action" color="amber" highlight={pending > 0} />
          <ReportStatCard label="Overdue" value={String(overdue)} sub="Past due date" color="red" highlight={overdue > 0} />
        </div>

        {/* Main two-column layout */}
        <div className="flex gap-5 flex-1 overflow-hidden p-6 pt-5">
          {/* Left: Report List */}
          <div className="w-80 flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
              <div className="text-sm font-semibold text-slate-700">All Regulatory Reports</div>
              <div className="text-xs text-slate-400">{REPORTS.length} total obligations</div>
            </div>
            <div className="flex-1 overflow-auto">
              {REPORTS.map(report => {
                const st = statusStyle(report.status);
                const fc = freqColor(report.frequency);
                const isSelected = selected.id === report.id;
                return (
                  <button
                    key={report.id}
                    onClick={() => setSelected(report)}
                    className={`w-full text-left px-4 py-3.5 border-b border-slate-100 transition-colors ${
                      isSelected ? 'bg-rose-50 border-l-2 border-l-rose-500' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="font-medium text-slate-900 text-sm leading-tight">{report.name}</div>
                      <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>
                        {report.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${fc}`}>
                        {report.frequency}
                      </span>
                      <span className="text-xs text-slate-400">{report.regulation}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
                      {report.status === 'Overdue' ? (
                        <span className="text-red-600 font-medium">Due: {report.nextDue} — OVERDUE</span>
                      ) : report.status === 'Pending' ? (
                        <span className="text-amber-600 font-medium">Due today by 16:00 HKT</span>
                      ) : (
                        <span>Next due: {report.nextDue}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Detail Panel */}
          <div className="flex-1 flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden">
            {/* Detail Header */}
            <div className="bg-gradient-to-r from-rose-600 to-rose-500 px-6 py-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-semibold text-rose-200 uppercase tracking-wide mb-1">
                    {selected.frequency} · {selected.regulation}
                  </div>
                  <div className="text-xl font-bold text-white">{selected.name}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className={`w-2 h-2 rounded-full ${statusStyle(selected.status).dot} ${
                    selected.status === 'Pending' ? 'animate-pulse' : ''
                  }`} />
                  <span className="text-white font-semibold text-sm">{selected.status}</span>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-6 space-y-5">
              {/* Description */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Report Description</div>
                <p className="text-sm text-slate-700 leading-relaxed">{selected.description}</p>
              </div>

              {/* Key Info Grid */}
              <div className="grid grid-cols-3 gap-3">
                <InfoBox label="Regulation" value={selected.regulation} />
                <InfoBox label="Frequency" value={selected.frequency} />
                <InfoBox label="Next Due" value={selected.nextDue} highlight={selected.status === 'Overdue' || selected.status === 'Pending'} />
                <InfoBox label="Last Submitted" value={selected.lastSubmitted} />
                <InfoBox label="Record Count" value={selected.recordCount !== undefined ? selected.recordCount.toLocaleString() : 'Pending'} />
                <InfoBox label="Status" value={selected.status} />
              </div>

              {/* Status Banner */}
              {selected.status === 'Overdue' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <div className="text-sm font-semibold text-red-800">Report Overdue by 1 Day</div>
                    <div className="text-xs text-red-600 mt-0.5">
                      Required submission date was {selected.nextDue}. Immediate submission required to avoid regulatory breach. 
                      Contact compliance officer: compliance@ailabquant.hk
                    </div>
                  </div>
                </div>
              )}
              {selected.status === 'Pending' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <div className="text-sm font-semibold text-amber-800">Submission Due Today by 16:00 HKT</div>
                    <div className="text-xs text-amber-700 mt-0.5">
                      Report is in draft state. Please generate, review, and submit before the daily deadline.
                    </div>
                  </div>
                </div>
              )}

              {/* Submission History */}
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Submission History (Last 5)
                </div>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">#</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Submission Date</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Records</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.submissionHistory.map((h, i) => {
                        const hs = histStatusStyle(h.status);
                        return (
                          <tr key={i} className="border-b border-slate-100 last:border-0">
                            <td className="px-4 py-3 text-xs text-slate-400">{i + 1}</td>
                            <td className="px-4 py-3 text-sm font-mono text-slate-700">{h.date}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${hs.bg} ${hs.text}`}>
                                {h.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-sm text-slate-600">
                              {h.records !== undefined ? h.records.toLocaleString() : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-2">
                <button className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Generate Report
                </button>
                <button
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    selected.status === 'Overdue'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : selected.status === 'Pending'
                      ? 'bg-rose-600 hover:bg-rose-700 text-white'
                      : 'bg-rose-100 hover:bg-rose-200 text-rose-700'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {selected.status === 'Submitted' ? 'Resubmit' : 'Submit to SFC'}
                </button>
                <button className="flex items-center gap-2 px-4 py-2.5 text-slate-500 hover:bg-slate-100 rounded-lg text-sm font-medium transition-colors ml-auto">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z" />
                  </svg>
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ReportStatCard({
  label, value, sub, color, highlight,
}: {
  label: string; value: string; sub: string; color: string; highlight?: boolean;
}) {
  const colorMap: Record<string, string> = {
    rose: 'text-rose-700', green: 'text-emerald-700',
    amber: 'text-amber-700', red: 'text-red-700',
  };
  return (
    <div className={`bg-white border rounded-xl p-4 ${highlight ? 'border-red-400 ring-1 ring-red-200' : 'border-slate-200'}`}>
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={`text-3xl font-bold ${colorMap[color] ?? 'text-slate-800'}`}>{value}</div>
      <div className="text-xs text-slate-400 mt-1">{sub}</div>
    </div>
  );
}

function InfoBox({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <div className={`text-sm font-semibold ${highlight ? 'text-red-700' : 'text-slate-800'}`}>{value}</div>
    </div>
  );
}